
import { supabase } from '../lib/supabase';

const isMissingScopeFunction = (error) =>
    error && (error.code === 'PGRST202' || /Could not find the function public\.get_scoped_/i.test(error.message || ''));

const canUseAdministrativeFallback = async () => {
    const { data: authData } = await supabase.auth.getUser();
    if (!authData?.user?.id) return false;
    const { data: profile } = await supabase.from('user_profiles').select('primary_role').eq('id', authData.user.id).maybeSingle();
    return ['system_administrator', 'chief_principal', 'vice_chancellor', 'vc', 'principal', 'deputy_principal_academic', 'registrar_academic'].includes(profile?.primary_role);
};

// A head-of-department role describes a management responsibility, not an
// academic discipline.  Keep the staff classification department-aware so a
// HOD in Estates (or another operational department) is not reported as
// academic staff simply because the role name is shared with academic HODs.
const normalizeRole = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const parseRoleList = (value) => {
    if (Array.isArray(value)) return value.map(normalizeRole).filter(Boolean);
    if (typeof value !== 'string' || !value.trim()) return [];

    const raw = value.trim();
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(normalizeRole).filter(Boolean);
    } catch (_) {
        // PostgreSQL array text and comma-separated legacy values are handled
        // by the fallback below.
    }

    return raw
        .replace(/^\{|\}$/g, '')
        .split(',')
        .map(item => normalizeRole(item.replace(/^"|"$/g, '')))
        .filter(Boolean);
};

const NON_ACADEMIC_DEPARTMENT_TERMS = [
    'estate',
    'transport',
    'hostel',
    'accommodation',
    'medical',
    'catering',
    'stores',
    'procurement',
    'finance',
    'security',
    'maintenance',
    'ict',
    'information technology',
    'corporate affairs',
    'student affairs',
    'administration',
];

const isNonAcademicDepartment = (user) => {
    const department = user?.department || user?.staff?.department || user?.department_details || {};
    if (typeof department?.is_academic === 'boolean') return !department.is_academic;
    if (typeof user?.is_academic_department === 'boolean') return !user.is_academic_department;
    // Academic departments are attached to a faculty in the institutional
    // hierarchy.  A department record with no faculty is therefore an
    // operational department (for example Estates), unless explicit metadata
    // above says otherwise.
    if (department?.id && Object.prototype.hasOwnProperty.call(department, 'faculty_id')) {
        if (!department.faculty_id) return true;
    }

    const departmentName = String(
        department?.name || user?.department_name || '',
    ).trim().toLowerCase();
    return NON_ACADEMIC_DEPARTMENT_TERMS.some(term => departmentName.includes(term));
};

const isAcademicStaffMember = (user, academicRoles = []) => {
    const roles = [user?.primary_role, ...parseRoleList(user?.secondary_roles)];
    const normalizedRoles = roles.map(normalizeRole).filter(Boolean);
    const headRole = normalizedRoles.includes('head_of_department') || normalizedRoles.includes('head_of_section');

    // HOD/HOS is academic only when attached to an academic department.  If
    // the department is not known yet, retain the historical academic default
    // so an incomplete profile is not silently stripped of access.
    if (headRole && isNonAcademicDepartment(user)) return false;

    const roleSet = academicRoles.length
        ? academicRoles.map(normalizeRole)
        : userService.ACADEMIC_ROLES.map(normalizeRole);
    return normalizedRoles.some(role => roleSet.includes(role));
};

export const userService = {
    getAllUsers: async () => {
        return userService.getUsers();
    },



    getUserById: async (id) => {
        if (!id) return null;
        const { data, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', id)
            .maybeSingle();
        if (error) {
            console.error('Error fetching user by id:', error);
            return null;
        }
        return data || null;
    },

    createUser: async (userData) => {
        try {
            const { data: session } = await supabase.auth.getSession();
            const token = session?.session?.access_token;
            const projectRef = import.meta.env.VITE_SUPABASE_URL.replace('https://', '').split('.')[0];
            const url = `https://${projectRef}.supabase.co/functions/v1/provision-staff-user`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(userData)
            });

            if (!response.ok) {
                let errorText = await response.text();
                try {
                    const json = JSON.parse(errorText);
                    errorText = json.error || json.message || errorText;
                } catch (e) { }
                throw new Error(errorText || `Server returned status ${response.status}`);
            }

            const result = await response.json();
            if (result?.success === false) {
                const reason = result.error || result.message || result.details || 'Staff account provisioning failed';
                throw new Error(reason);
            }
            return { success: result?.success !== false, ...result };
        } catch (error) {
            console.error('Fetch error:', error);
            throw new Error(error.message || 'Staff account provisioning failed');
        }
    },

    updateUser: async (id, userData) => {
        console.log(`Updating user ${id}:`, userData);
        return userService.updateUserProfile(id, userData);
    },

    ACADEMIC_ROLES: [
        'trainer',
        'lecturer',
        'trainer_lecturer',
        'teacher',
        'tutor',
        'instructor',
        'academic',
        'chief_principal',
        'vice_chancellor',
        'vc',
        'principal',
        'deputy_principal_academic',
        'deputy_principal_administration',
        'registrar_academic',
        'dean_of_faculty',
        'head_of_department',
        'head_of_section',
        'poe_manager',
        'timetable_master',
        'assessment_officer',
        'quality_assurance_officer',
        'finance_officer',
        'dean_of_students'
    ],

    // Shared by user-management views and dashboards so HOD/HOS records are
    // classified using their department rather than the title alone.
    isAcademicStaffMember,

    // Fetch users with optional filters
    getUsers: async (filters = {}) => {
        try {
            const studentMode = filters.role === 'student';
            const allRolesMode = !filters.role || filters.role === 'all';
            const [studentResult, staffResult] = await Promise.all([
                studentMode || allRolesMode ? supabase.rpc('get_scoped_students') : Promise.resolve({ data: [], error: null }),
                !studentMode ? supabase.rpc('get_scoped_staff_users') : Promise.resolve({ data: [], error: null })
            ]);
            const missingStudentScope = isMissingScopeFunction(studentResult.error);
            const missingStaffScope = isMissingScopeFunction(staffResult.error);
            if ((studentResult.error && !missingStudentScope) || (staffResult.error && !missingStaffScope)) throw studentResult.error || staffResult.error;
            const needsFallback = missingStudentScope || missingStaffScope;
            if (needsFallback && !(await canUseAdministrativeFallback())) throw studentResult.error || staffResult.error;

            let studentSource = studentResult.data || [];
            let staffSource = staffResult.data || [];
            if (missingStudentScope) {
                const fallback = await supabase.from('students').select('*');
                if (fallback.error) throw fallback.error;
                studentSource = fallback.data || [];
            }
            if (missingStaffScope) {
                const fallback = await supabase.from('user_profiles').select('*').neq('primary_role', 'student');
                if (fallback.error) throw fallback.error;
                staffSource = fallback.data || [];
            }

            const scopedStudents = studentSource.map(row => {
                const item = row.student || row;
                const studentName = [item.first_name, item.middle_name, item.last_name].filter(Boolean).join(' ').trim();
                return { ...item, id: item.profile_id, full_name: item.full_name || studentName || null, name: item.full_name || studentName || null, email: item.institutional_email || item.email, status: item.profile_status || String(item.status || 'active').toLowerCase(), primary_role: 'student' };
            });
            const scopedStaff = staffSource.map(row => row.staff_user || row);
            let userData = [...scopedStudents, ...scopedStaff];
            if (filters.search) { const term=filters.search.toLowerCase(); userData=userData.filter(row=>[row.full_name,row.email,row.employee_id,row.admission_number].some(value=>String(value||'').toLowerCase().includes(term))); }
            if (filters.status && filters.status !== 'all') userData=userData.filter(row=>String(row.status).toLowerCase()===String(filters.status).toLowerCase());

            const profileIds = (userData || []).map(user => user.id);
            const { data: profileRows, error: profileError } = profileIds.length
                ? await supabase.from('user_profiles').select('id,full_name,first_name,middle_name,last_name,email,status').in('id', profileIds)
                : { data: [], error: null };
            if (profileError) console.warn('Profile names could not be loaded:', profileError);
            const profileMap = (profileRows || []).reduce((map, row) => ({ ...map, [row.id]: row }), {});
            const { data: staffRows, error: staffError } = profileIds.length
                ? await supabase.from('staff').select('*').in('profile_id', profileIds)
                : { data: [], error: null };
            if (staffError) console.warn('Staff details could not be loaded:', staffError);
            const staffMap = (staffRows || []).reduce((map, row) => ({ ...map, [row.profile_id]: row }), {});
            const { data: studentRows, error: studentError } = profileIds.length
                ? await supabase.from('students').select('*').in('profile_id', profileIds)
                : { data: [], error: null };
            if (studentError) console.warn('Student details could not be loaded:', studentError);
            const studentMap = (studentRows || []).reduce((map, row) => ({ ...map, [row.profile_id]: row }), {});

            const programIds = [...new Set((studentRows || []).map(row => row.program_id).filter(Boolean))];
            const { data: programRows } = programIds.length
                ? await supabase.from('programs').select('id,name,code,department_id').in('id', programIds)
                : { data: [] };
            const programMap = (programRows || []).reduce((map, row) => ({ ...map, [row.id]: row }), {});

            // Fetch departments separately to map them manually
            // This avoids issues if the join relationship is broken or complex
            const { data: deptData } = await supabase.from('departments').select('id, name, code, faculty_id');
            const deptMap = (deptData || []).reduce((acc, d) => {
                if (d && d.id) acc[d.id] = d;
                return acc;
            }, {});

            // Process users to attach department info
            const processedData = (userData || []).map(user => {
                if (!user) return null;

                let deptVal = null;
                // Identify staff record
                const staffRec = staffMap[user.id];
                const studentRec = studentMap[user.id];
                const profileRec = profileMap[user.id];
                if (staffRec) {
                    if (staffRec && staffRec.department_id) {
                        deptVal = deptMap[staffRec.department_id] || null;
                    }
                }

                // Attach as 'department' property to be friendly to UI
                return {
                    ...user,
                    full_name: profileRec?.full_name || user.full_name || [studentRec?.first_name, studentRec?.middle_name, studentRec?.last_name].filter(Boolean).join(' ') || null,
                    name: profileRec?.full_name || user.name || user.full_name || [studentRec?.first_name, studentRec?.middle_name, studentRec?.last_name].filter(Boolean).join(' ') || null,
                    staff: staffRec || null,
                    student: studentRec || null,
                    employee_id: staffRec?.employee_id || null,
                    student_id: studentRec?.admission_number || user.student_id || null,
                    student_ID: studentRec?.admission_number || user.student_id || null,
                    admission_number: studentRec?.admission_number || null,
                    institutional_email: staffRec?.institutional_email || studentRec?.institutional_email || user.email || null,
                    personal_email: studentRec?.personal_email || user.personal_email || null,
                    program_id: studentRec?.program_id || null,
                    program: studentRec?.program_id ? programMap[studentRec.program_id] || null : null,
                    academic_level: studentRec?.academic_level || null,
                    intake_code: studentRec?.intake_code || null,
                    enrollment_status: studentRec?.enrollment_status || null,
                    job_title: staffRec?.job_title || null,
                    department: deptVal
                };
            }).filter(Boolean); // Remove any nulls

            // Client-side filtering
            let filteredData = processedData;

            if (filters.role && filters.role !== 'all') {
                const academicRoles = userService.ACADEMIC_ROLES.map(normalizeRole);

                filteredData = processedData.filter(user => {
                    if (!user) return false;

                    const pRole = normalizeRole(user.primary_role);

                    // Parse secondary roles
                    let sRoles = [];
                    if (Array.isArray(user.secondary_roles)) {
                        sRoles = user.secondary_roles.map(normalizeRole);
                    } else if (typeof user.secondary_roles === 'string') {
                        // Handle potential JSON string or CSV
                        const cleaned = user.secondary_roles.trim();
                        if (cleaned.startsWith('[') && cleaned.endsWith(']')) {
                            try {
                                sRoles = JSON.parse(cleaned).map(normalizeRole);
                            } catch (e) {
                                console.warn('Failed to parse secondary_roles JSON:', user.secondary_roles);
                                // Fallback to basic split if JSON parse fails? Or just empty.
                                // Let's try to be lenient
                                sRoles = cleaned.replace(/[\[\]"]/g, '').split(',').map(normalizeRole);
                            }
                        } else {
                            sRoles = cleaned.split(',').map(normalizeRole);
                        }
                    }

                    // Check for specific role match
                    const targetRole = normalizeRole(filters.role);

                    // Special keyword handling
                    if (targetRole === 'all_staff') {
                        const isStudent = pRole === 'student' || sRoles.includes('student');
                        return !isStudent;
                    } else if (targetRole === 'academic') {
                        return isAcademicStaffMember(user, academicRoles);
                    } else if (targetRole === 'non_academic') {
                        const isStudent = pRole === 'student' || sRoles.includes('student');
                        const isAcademic = isAcademicStaffMember(user, academicRoles);
                        return !isStudent && !isAcademic;
                    } else if (targetRole === 'student') {
                        return pRole === 'student' || sRoles.includes('student');
                    } else {
                        // Exact match for specific role (e.g. 'trainer_lecturer')
                        return pRole === targetRole || sRoles.includes(targetRole);
                    }
                });
            }

            return filteredData;
        } catch (error) {
            console.error('Error fetching users:', error);
            return [];
        }
    },

    getCurrentUserProfile: async (userId) => {
        try {
            const { data: profileData, error } = await supabase.from('user_profiles').select('*').eq('id', userId).single();
            if (error) throw error;
            const [{ data: staffData }, { data: studentData }] = await Promise.all([
                supabase.from('staff').select('*').eq('profile_id', userId).maybeSingle(),
                supabase.from('students').select('*').eq('profile_id', userId).maybeSingle()
            ]);
            const departmentId = staffData?.department_id || profileData.department_id;
            const { data: departmentData } = departmentId
                ? await supabase.from('departments').select('id,name,code,faculty_id').eq('id', departmentId).maybeSingle()
                : { data: null };
            return { ...profileData, staff: staffData ? { ...staffData, department: departmentData || null } : null, students: studentData ? [studentData] : [], department: departmentData || null };
        } catch (error) {
            console.error('Error fetching user profile:', error);
            // A profile must come from the database. Never expose placeholder users.
            return null;
        }
    },

    updateUserProfile: async (userId, updates) => {
        try {
            const { data, error } = await supabase.rpc('admin_update_staff_user', {
                target_user: userId,
                payload: updates
            });
            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error updating user profile:', error);
            return { success: false, error };
        }
    },

    // Toggle User Status (Active/Inactive/Deactivated)
    updateUserStatus: async (userId, newStatus) => {
        return userService.updateUserProfile(userId, { status: newStatus });
    },

    deleteUser: async (userId) => {
        try {
            if (!userId) throw new Error('A user account is required');
            const { data, error } = await supabase.rpc('admin_delete_user_account', {
                p_target_user: userId
            });
            if (error) throw error;
            return { success: true, data };
        } catch (error) {
            console.error('Error deleting user:', error);
            return { success: false, error };
        }
    },

    resetUserPassword: async (email) => {
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: window.location.origin + '/reset-password',
            });
            if (error) throw error;
            return { success: true };
        } catch (error) {
            console.error('Error sending password reset:', error);
            return { success: false, error };
        }
    },

    /**
     * Reset an account to a valid institutional temporary password (admission
     * number for students, PF-prefixed employee number for staff). The database function enforces
     * the system-administrator check and marks the account for a password
     * change at next login.
     */
    resetPasswordToIdentity: async (userId) => {
        try {
            if (!userId) throw new Error('A user account is required');
            const { data, error } = await supabase.rpc('admin_reset_identity_password', {
                p_target_user: userId
            });
            if (error) throw error;
            if (!data?.temporary_password) throw new Error('The password reset did not return a temporary password');
            return {
                success: true,
                ...data
            };
        } catch (error) {
            console.error('Error resetting password to institutional identity:', error);
            return { success: false, error };
        }
    },

    getCategorizedUserCounts: async () => {
        try {
            // Student Management is the source of truth for trainee numbers. A
            // student may exist before an Auth/user profile is provisioned, so
            // counting only user_profiles understates the real student total.
            const [profilesResult, studentsResult] = await Promise.all([
                supabase.from('user_profiles').select('id, primary_role, secondary_roles, status'),
                supabase.from('students').select('profile_id, source_application_id, status, enrollment_status')
            ]);
            if (profilesResult.error) throw profilesResult.error;
            if (studentsResult.error) throw studentsResult.error;

            const isActiveStatus = value => String(value || '').trim().toLowerCase() === 'active';
            const users = (profilesResult.data || []).filter(profile => isActiveStatus(profile.status));
            const students = studentsResult.data || [];
            // Student Management contains the institution's admitted student
            // population, so this figure must match its unfiltered list exactly.
            // Account provisioning is a lifecycle attribute, not a reason to
            // remove an admitted learner from the institutional headcount.
            const activeAdmittedStudents = students;
            const studentProfileIds = new Set(activeAdmittedStudents.map(student => student.profile_id).filter(Boolean));
            const normalize = (role) => (role || '').toLowerCase().trim();
            const rolesFor = profile => {
                const roles = [normalize(profile.primary_role)];
                if (Array.isArray(profile.secondary_roles)) return [...roles, ...profile.secondary_roles.map(normalize)];
                if (typeof profile.secondary_roles === 'string' && profile.secondary_roles.trim()) {
                    if (profile.secondary_roles.trim().startsWith('[')) {
                        try { return [...roles, ...JSON.parse(profile.secondary_roles).map(normalize)]; } catch (error) { /* use CSV fallback */ }
                    }
                    return [...roles, ...profile.secondary_roles.split(',').map(normalize)];
                }
                return roles;
            };
            const eligibleProfiles = users.filter(profile => {
                const hasStudentRole = rolesFor(profile).includes('student');
                return !hasStudentRole || studentProfileIds.has(profile.id);
            });
            const eligibleProfileIds = new Set(eligibleProfiles.map(profile => profile.id));
            const admittedStudentsWithoutEligibleProfile = activeAdmittedStudents.filter(student =>
                !student.profile_id || !eligibleProfileIds.has(student.profile_id)
            ).length;

            const counts = {
                total: eligibleProfiles.length + admittedStudentsWithoutEligibleProfile,
                students: activeAdmittedStudents.length,
                academic: 0, // Renamed from lecturers to academic for clarity with new requirements
                staff: 0,    // Non-Academic
                admins: 0
            };

            // HOD/HOS classification needs the department attached to the
            // profile.  Staff stores the authoritative department assignment,
            // so load it once for the count calculation instead of treating
            // every HOD as academic.
            const profileIds = eligibleProfiles.map(profile => profile.id).filter(Boolean);
            const [{ data: staffRows }, { data: departmentRows }] = await Promise.all([
                profileIds.length
                    ? supabase.from('staff').select('profile_id, department_id').in('profile_id', profileIds)
                    : Promise.resolve({ data: [] }),
                supabase.from('departments').select('id, name, faculty_id')
            ]);
            const staffByProfile = (staffRows || []).reduce((map, row) => {
                map[row.profile_id] = row;
                return map;
            }, {});
            const departmentsById = (departmentRows || []).reduce((map, row) => {
                map[row.id] = row;
                return map;
            }, {});

            const academicRoles = userService.ACADEMIC_ROLES.map(normalize);

            eligibleProfiles.forEach(u => {
                const pRole = normalize(u.primary_role);

                // Parse secondary roles
                let sRoles = [];
                if (Array.isArray(u.secondary_roles)) {
                    sRoles = u.secondary_roles.map(normalize);
                } else if (typeof u.secondary_roles === 'string') {
                    if (u.secondary_roles.trim().startsWith('[')) {
                        try { sRoles = JSON.parse(u.secondary_roles).map(normalize); } catch (e) { }
                    } else {
                        sRoles = u.secondary_roles.split(',').map(normalize);
                    }
                }

                // Check Categories
                const isStudent = studentProfileIds.has(u.id) || pRole.includes('student') || sRoles.some(r => r.includes('student'));
                const staffRecord = staffByProfile[u.id];
                const department = staffRecord?.department_id
                    ? departmentsById[staffRecord.department_id] || null
                    : null;
                const isAcademic = isAcademicStaffMember({
                    ...u,
                    department,
                    // Keep the normalized role parsing above as the source for
                    // ordinary roles, while the helper handles HOD/HOS scope.
                    primary_role: pRole,
                    secondary_roles: sRoles,
                }, academicRoles);
                const isAdmin = pRole.includes('admin') || sRoles.some(r => r.includes('admin'));

                // Increment Counts (Overlaps Allowed)
                if (isAcademic) counts.academic++;
                if (isAdmin) counts.admins++; // Kept separate or part of staff? Assuming separate KPI.

                // Non-Academic ("Staff"): Anyone NOT Student AND NOT Academic.
                // "academic staff may as well work on none academic, let them again be counted" -> This implies overlap?
                // User said: "if they have another role qualifying non academic ... let them be counted."
                // But simplified: Non-Academic usually means Admin, HR, Support etc.
                // If I strictly follow: if NOT student AND NOT academic ?? 
                // OR checking specific non-academic roles?
                // Given undefined non-academic list, I will use: NOT Student AND NOT Academic.
                // Wait, "let them again be counted" implies if they have strict non-academic roles.
                // Since I don't have a list for non-academic, I'll count 'staff' as 'others'. 

                if (!isStudent && !isAcademic) {
                    counts.staff++;
                }
            });

            return counts;

        } catch (error) {
            console.error('Error fetching categorized users:', error);
            throw error;
        }
    },

    // Fetch all available roles
    getRoles: async () => {
        try {
            console.log('Fetching roles from role_permissions...');
            const { data, error } = await supabase
                .from('role_permissions')
                .select('role') // We only need the role name
                .order('role');

            if (error) {
                console.error('Supabase error fetching roles:', error);
                throw error;
            }

            // Deduplicate roles (in case table has multiple rows per role, e.g. permissions)
            const uniqueRoleNames = [...new Set((data || []).map(item => item.role).filter(Boolean))];
            // Keep the verifier assignable while deployments catch up with the
            // role_permissions seed migration.
            if (!uniqueRoleNames.includes('external_poe_verifier')) {
                uniqueRoleNames.push('external_poe_verifier');
            }
            const uniqueRoles = uniqueRoleNames.sort().map(role => ({ role }));

            console.log('Roles fetched successfully (unique):', uniqueRoles);
            return uniqueRoles;
        } catch (error) {
            console.error('Error fetching roles:', error);
            return [];
        }
    }
};
